import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

/**
 * AudioFocus — global "only-one-iframe-active-at-a-time" coordinator.
 *
 * Multiple Federation Radio embeds may render on the same page (homepage
 * body, hub Column 2).  To prevent double-audio, each embed registers
 * itself via useAudioFocusSlot(slotId) and receives:
 *   • isActive: boolean — render the real iframe when true, else a
 *     lightweight placeholder tile that says "signal parked"
 *   • wrapperRef: attach to the container that IntersectionObserver
 *     watches to decide activation
 *
 * Rules:
 *   • The first slot to become ≥50% visible claims focus.
 *   • Subsequent visible slots take over.
 *   • The /radio standalone page does NOT participate — it renders its
 *     iframe directly with no context guard.
 */

const AudioFocusContext = createContext({
  activeSlot: null,
  claim: () => {},
});

export function AudioFocusProvider({ children }) {
  const [activeSlot, setActiveSlot] = useState(null);

  const claim = useCallback((slotId) => {
    setActiveSlot((prev) => (prev === slotId ? prev : slotId));
  }, []);

  return (
    <AudioFocusContext.Provider value={{ activeSlot, claim }}>
      {children}
    </AudioFocusContext.Provider>
  );
}

export function useAudioFocusSlot(slotId) {
  const { activeSlot, claim } = useContext(AudioFocusContext);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!wrapperRef.current) return undefined;
    const el = wrapperRef.current;
    // First slot to appear gets focus. If none active yet AND this
    // element enters the viewport, claim.  Later intersections keep
    // claiming so the most-recently-viewed slot always wins.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            claim(slotId);
          }
        });
      },
      { threshold: [0, 0.5, 1] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [slotId, claim]);

  // If nothing has claimed focus yet, the FIRST slot that mounts should
  // hold the iframe so the page never renders zero radios.  This makes
  // the homepage body (which mounts first, above the hub) the default.
  useEffect(() => {
    if (activeSlot === null) claim(slotId);
  }, []); // intentional: run once on mount to seed initial focus

  return {
    isActive: activeSlot === slotId,
    wrapperRef,
  };
}
